import { useMemo, useState } from "react";

import {
  InfoCircleFill,
  LockFill,
} from "@hl/base-components/lib/assets/icons.generated/index";
import { Group, TextInput } from "@mantine/core";
import { TextInputProps } from "@mantine/core/lib/TextInput/TextInput";
import { ethers } from "ethers";

import { TokenDistributionType } from "apollo/graphql.generated";
import { ALCHEMY_API_KEY_ENS } from "config";
import PaymentRecipientInfoModal from "~/features/tokens/components/AdvancedFormModals/PaymentRecipientInfoModal";

type CustomRecipient = {
  ensName?: string | null;
  address?: string | null;
};

type Props = {
  onChangeLoading: (loading: boolean) => void;
  onError: (error: string) => void;
  onChange: (customRecipient: CustomRecipient) => void;
  addressInputProps: TextInputProps;
  distributionType: TokenDistributionType;
};

export const PayoutAddressInput: React.FC<Props> = ({
  onChangeLoading,
  onChange,
  onError,
  addressInputProps,
  distributionType,
}) => {
  const [paymentRecipientModalOpen, setPaymentRecipientModalOpen] =
    useState<boolean>(false);

  const ensUrl = `https://eth-mainnet.alchemyapi.io/v2/${ALCHEMY_API_KEY_ENS}`;
  const provider = useMemo(
    () =>
      ALCHEMY_API_KEY_ENS
        ? new ethers.providers.JsonRpcProvider(ensUrl)
        : ethers.getDefaultProvider(),
    [ensUrl]
  );

  const isAuction = distributionType === TokenDistributionType.Auction;

  const onSetPaymentRecipientEOA = async (val: string) => {
    if (addressInputProps.disabled) {
      return;
    }

    if (val.endsWith(".eth")) {
      onChangeLoading(true);
      try {
        const address = await provider.resolveName(val);
        if (address) {
          onChange({
            address,
            ensName: val,
          });
        } else {
          onError("Invalid ENS");
        }
      } catch (e) {
        // addressInputProps.error("....") ???
        onError(
          "Error fetching ENS. Try later or use the actual wallet address."
        );
      } finally {
        onChangeLoading(false);
      }
    } else {
      onChange({
        address: val,
      });
    }
  };

  return (
    <>
      <TextInput
        placeholder="e.g. vitalik.eth, 0x0000000000000000000000000000000000000000"
        onInput={(e) => onSetPaymentRecipientEOA(e.currentTarget.value)}
        spellCheck={false}
        label={
          <>
            <Group spacing={4}>
              {isAuction ? "Auction" : "Primary sales"} payout address
              <InfoCircleFill
                width={17}
                height={17}
                stroke={"#000000"}
                cursor={"pointer"}
                onClick={() => setPaymentRecipientModalOpen(true)}
              />
            </Group>
          </>
        }
        rightSection={
          addressInputProps.disabled ? (
            <LockFill width={20} height={20} />
          ) : undefined
        }
        {...addressInputProps}
      />
      <PaymentRecipientInfoModal
        isOpen={paymentRecipientModalOpen}
        onClose={() => setPaymentRecipientModalOpen(false)}
      />
    </>
  );
};
